import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ClipboardCheck, Receipt, Star, Trash2, Pencil, Play, Search, Package, Clock, User, Check, X, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { Card, Badge, Modal } from "@/components/common";
import { api } from "@/lib/api";
import { Inhalt } from "@/components/bausteine/BausteineAnsicht"; // gleiche Darstellung wie die Bausteine (eine Stelle)

// Wiederverwendbare Komponente fuer Dokument-Vorlagen (Angebot / Auftragsbestaetigung / Rechnung)
// Wird sowohl in Einstellungen als Tab als auch im Dokumente-Modul eingebunden.

const TYPE_META = {
  quote: { label: "Angebot", icon: FileText, color: "blue", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  order: { label: "Auftragsbestätigung", icon: ClipboardCheck, color: "purple", bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  invoice: { label: "Rechnung", icon: Receipt, color: "green", bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
};

// auswahl = { docType, onUebernehmen(tpl) }: Auswahl-Modus im Beleg-Fenster („Vorlage öffnen“, 01.11.2026).
// Dieselbe Liste wie in den Einstellungen; Bearbeiten, Umbenennen, Löschen und „Verwenden“ gibt es nur dort.
export const DocumentTemplatesPanel = ({ variant = "page", auswahl = null }) => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(auswahl?.docType || "all");
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [editTpl, setEditTpl] = useState(null);
  const [useTpl, setUseTpl] = useState(null);
  const [ansehenTpl, setAnsehenTpl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/document-templates");
      setList(res.data);
    } catch { toast.error("Fehler beim Laden der Vorlagen"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Eigenes Fenster: die Belegart des offenen Belegs kommt erst nach der Anfrage
  useEffect(() => { if (auswahl?.docType) setTypeFilter(auswahl.docType); }, [auswahl?.docType]);

  const filtered = list
    .filter(t => typeFilter === "all" || t.doc_type === typeFilter)
    .filter(t => !onlyFavorites || t.favorite)
    .filter(t => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (t.name || "").toLowerCase().includes(q) ||
             JSON.stringify(t.snapshot?.positions || []).toLowerCase().includes(q);
    });

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Vorlage "${tpl.name}" wirklich löschen?`)) return;
    try { await api.delete(`/document-templates/${tpl.id}`); toast.success("Gelöscht"); load(); }
    catch { toast.error("Fehler"); }
  };

  // Inhalt im gewohnten Beleg-Editor ändern: Arbeits-Beleg (ohne Nummer, in keiner Liste) wird angelegt oder wiederverwendet
  const inhaltBearbeiten = async (tpl) => {
    try {
      const res = await api.post(`/document-templates/${tpl.id}/bearbeiten`);
      navigate(res.data.edit_url);
    } catch { toast.error("Vorlage konnte nicht geöffnet werden"); }
  };

  const toggleFavorite = async (tpl) => {
    try {
      await api.put(`/document-templates/${tpl.id}`, { favorite: !tpl.favorite });
      load();
    } catch { toast.error("Fehler"); }
  };

  const handleUse = async (tpl) => {
    // Dokument direkt aus Vorlage anlegen (ohne Kunde) – User kann im Editor Kunde zuweisen
    try {
      const res = await api.post(`/document-templates/${tpl.id}/create-document`, {});
      toast.success(`${TYPE_META[tpl.doc_type].label} aus Vorlage erstellt`);
      navigate(res.data.edit_url);
    } catch { toast.error("Fehler beim Anlegen"); }
  };

  const stats = {
    total: list.length,
    quote: list.filter(t => t.doc_type === "quote").length,
    order: list.filter(t => t.doc_type === "order").length,
    invoice: list.filter(t => t.doc_type === "invoice").length,
    favorites: list.filter(t => t.favorite).length,
  };

  const container = variant === "page" ? "max-w-7xl mx-auto" : "";

  return (
    <div className={container} data-testid="doc-templates-panel">
      {variant === "page" && (
        <div className="mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2"><Package className="w-5 h-5" /> Dokument-Vorlagen</h2>
          <p className="text-sm text-muted-foreground mt-1">Wiederverwendbare Angebote, Auftragsbestätigungen und Rechnungen.</p>
        </div>
      )}

      {/* Filter-Leiste */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Vorlage suchen..."
            className="w-full pl-10 pr-3 py-2 border rounded-sm text-sm"
            data-testid="tpl-search"
          />
        </div>
        <div className="flex gap-1">
          {[
            { id: "all", label: "Alle", count: stats.total },
            { id: "quote", label: "Angebote", count: stats.quote },
            { id: "order", label: "Aufträge", count: stats.order },
            { id: "invoice", label: "Rechnungen", count: stats.invoice },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTypeFilter(t.id)}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${typeFilter === t.id ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}
              data-testid={`tpl-filter-${t.id}`}
            >
              {t.label} {t.count > 0 && <span className="opacity-70">({t.count})</span>}
            </button>
          ))}
        </div>
        <button
          onClick={() => setOnlyFavorites(!onlyFavorites)}
          className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors flex items-center gap-1 ${onlyFavorites ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-background hover:bg-muted border-border"}`}
          data-testid="tpl-favorite-toggle"
        >
          <Star className={`w-3.5 h-3.5 ${onlyFavorites ? "fill-amber-500" : ""}`} /> Nur Favoriten
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Lade...</div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <div className="font-medium mb-1">{list.length === 0 ? "Noch keine Vorlagen" : "Keine passenden Vorlagen"}</div>
          <div className="text-sm">
            {list.length === 0
              ? "Öffne ein Angebot/Rechnung im Editor und klicke auf 'Als Vorlage speichern' um eine zu erstellen."
              : "Passe Filter oder Suche an."}
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(tpl => {
            const meta = TYPE_META[tpl.doc_type] || TYPE_META.quote;
            const Icon = meta.icon;
            const positions = tpl.snapshot?.positions?.length || 0;
            const total = tpl.snapshot?.total_gross || 0;
            return (
              <Card key={tpl.id} className="p-3 hover:shadow-md transition-shadow" data-testid={`tpl-row-${tpl.id}`}>
                <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                  <div className={`p-2 rounded-sm ${meta.bg} flex-shrink-0`}>
                    <Icon className={`w-5 h-5 ${meta.text}`} />
                  </div>
                  <div className="flex-1 min-w-0 basis-[calc(100%-3.5rem)] sm:basis-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold truncate">{tpl.name}</h3>
                      <Badge className={`${meta.bg} ${meta.text} text-xs border ${meta.border}`}>{meta.label}</Badge>
                      {tpl.favorite && <Star className="w-4 h-4 fill-amber-500 text-amber-500" />}
                      {tpl.anonymized && <Badge className="bg-slate-100 text-slate-600 text-xs">Mustermann</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-3 mt-1">
                      <span className="flex items-center gap-1"><Package className="w-3 h-3" />{positions} Position{positions !== 1 ? "en" : ""}</span>
                      {total > 0 && <span className="font-mono">{total.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>}
                      {tpl.usage_count > 0 && <span>{tpl.usage_count}× genutzt</span>}
                      {tpl.last_used_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(tpl.last_used_at).toLocaleDateString("de-DE")}</span>}
                    </div>
                  </div>
                  <div className="w-full sm:w-auto flex items-center gap-1 flex-wrap sm:flex-nowrap sm:flex-shrink-0">
                    <button
                      onClick={() => toggleFavorite(tpl)}
                      className="p-2 hover:bg-amber-50 rounded-sm text-muted-foreground hover:text-amber-500 transition-colors"
                      title={tpl.favorite ? "Favorit entfernen" : "Als Favorit markieren"}
                      data-testid={`tpl-star-${tpl.id}`}
                    >
                      <Star className={`w-4 h-4 ${tpl.favorite ? "fill-amber-500 text-amber-500" : ""}`} />
                    </button>
                    <button
                      onClick={() => setAnsehenTpl(tpl)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-border rounded-sm text-xs font-medium hover:bg-muted"
                      title="Positionen der Vorlage ansehen"
                      data-testid={`tpl-ansehen-${tpl.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Ansehen
                    </button>
                    {auswahl && (
                      <button
                        onClick={() => auswahl.onUebernehmen(tpl)}
                        disabled={!!auswahl.gesperrt}
                        title={auswahl.gesperrt ? auswahl.gesperrtHinweis : undefined}
                        className="disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-sm text-xs font-medium hover:bg-primary/90"
                        data-testid={`tpl-uebernehmen-${tpl.id}`}
                      >
                        <Download className="w-3.5 h-3.5" /> Übernehmen
                      </button>
                    )}
                    {!auswahl && <button
                      onClick={() => setUseTpl(tpl)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-sm text-xs font-medium hover:bg-primary/90"
                      data-testid={`tpl-use-${tpl.id}`}
                    >
                      <Play className="w-3.5 h-3.5" /> Verwenden
                    </button>}
                    {!auswahl && <button
                      onClick={() => setEditTpl(tpl)}
                      className="p-2 hover:bg-blue-50 rounded-sm text-blue-600 transition-colors"
                      title="Name ändern"
                      aria-label="Name ändern"
                      data-testid={`tpl-rename-${tpl.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>}
                    {!auswahl && <button
                      onClick={() => inhaltBearbeiten(tpl)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-blue-300 text-blue-700 rounded-sm text-xs font-medium hover:bg-blue-50"
                      title="Positionen, Texte und Preise der Vorlage im Beleg-Fenster ändern"
                      data-testid={`tpl-inhalt-${tpl.id}`}
                    >
                      <FileText className="w-3.5 h-3.5" /> Inhalt bearbeiten
                    </button>}
                    {!auswahl && <button
                      onClick={() => handleDelete(tpl)}
                      className="p-2 hover:bg-red-50 rounded-sm text-red-500 transition-colors"
                      title="Löschen"
                      data-testid={`tpl-delete-${tpl.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {editTpl && <RenameTemplateModal tpl={editTpl} onClose={() => setEditTpl(null)} onSaved={() => { setEditTpl(null); load(); }} />}
      {ansehenTpl && (
        <VorlageAnsehen
          tpl={ansehenTpl}
          auswahl={auswahl}
          onZurueck={() => setAnsehenTpl(null)}
          onUebernehmen={() => { const t = ansehenTpl; setAnsehenTpl(null); auswahl.onUebernehmen(t); }}
          onFuerKunden={() => { const t = ansehenTpl; setAnsehenTpl(null); setUseTpl(t); }}
        />
      )}
      {useTpl && <UseTemplateModal tpl={useTpl} onClose={() => setUseTpl(null)} onStarted={() => { setUseTpl(null); load(); }} />}
    </div>
  );
};


// ===== Ansehen: schlichte Vorschau der Positionen (kein Briefkopf, keine Anschrift) =====
const euro = (n) => (Number(n) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
// Texte der Vorlage lesbar machen: HTML-Reste weg, Platzhalter als [Anrede des Kunden] usw.
const PLATZHALTER = { anrede_brief: "Anrede des Kunden", kunde_name: "Name des Kunden", kunde_adresse: "Adresse des Kunden", kunde_email: "E-Mail des Kunden", kunde_telefon: "Telefon des Kunden", firma: "Firma", datum: "Datum", dokument_nr: "Belegnummer", bearbeiter: "Bearbeiter", zahlbar_bis: "Zahlungsziel", bankverbindung: "Bankverbindung" };
const textLesbar = (t) => String(t || "")
  .replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>\s*<p>/gi, "\n\n").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ")
  .replace(/\{([a-z_]+)\}/g, (m, k) => `[${PLATZHALTER[k] || k.replace(/_/g, " ")}]`)
  .trim();
const zahl = (n) => (Number(n) || 0).toLocaleString("de-DE", { maximumFractionDigits: 3 });

const VorlageAnsehen = ({ tpl, auswahl, onZurueck, onUebernehmen, onFuerKunden }) => {
  const meta = TYPE_META[tpl.doc_type] || TYPE_META.quote;
  const snap = tpl.snapshot || {};
  const positionen = snap.positions || [];
  const echte = positionen.filter(p => p.type !== "titel");
  const summe = echte.reduce((s, p) => s + (Number(p.quantity) || 0) * (Number(p.price_net) || 0), 0);
  const rabatt = Number(snap.discount) || 0;
  const prozent = (snap.discount_type || "percent") === "percent";
  const rabattBetrag = rabatt > 0 ? (prozent ? summe * (rabatt / 100) : rabatt) : 0;
  const netto = summe - rabattBetrag;
  const mwstSatz = snap.vat_rate !== undefined && snap.vat_rate !== null ? Number(snap.vat_rate) : 19;
  const mwst = netto * (mwstSatz / 100);
  return (
    <div className="fixed inset-0 bg-black/50 z-[90] flex items-stretch sm:items-center justify-center sm:p-4" data-testid="tpl-ansehen-fenster">
      <div className="bg-background sm:rounded-lg shadow-xl w-full sm:max-w-5xl h-full sm:h-auto sm:max-h-[90vh] flex flex-col">
        <div className="p-3 sm:p-4 border-b flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold break-words" data-testid="tpl-ansehen-name">{tpl.name}</h3>
            <Badge className={`${meta.bg} ${meta.text} text-xs border ${meta.border} mt-1`}>{meta.label}</Badge>
          </div>
          <button onClick={onZurueck} className="p-1 hover:bg-muted rounded-sm" aria-label="Schließen" data-testid="tpl-ansehen-x">✕</button>
        </div>
        <div className="p-2 sm:p-4 overflow-y-auto flex-1 min-h-0">
          {textLesbar(snap.vortext) && <p className="mb-2 px-1 text-xs text-muted-foreground whitespace-pre-line break-words" data-testid="tpl-ansehen-vortext">{textLesbar(snap.vortext)}</p>}
          {positionen.length === 0 ? (
            <div className="text-sm text-muted-foreground p-4 text-center">Diese Vorlage hat keine Positionen.</div>
          ) : (
            <div data-testid="tpl-ansehen-tabelle"><Inhalt b={{ id: tpl.id, positions: positionen }} woerter={[]} ohneHaken /></div>
          )}
          <div className="mt-3 text-sm space-y-0.5 text-right" data-testid="tpl-ansehen-summe">
            {rabattBetrag > 0 && <div className="text-muted-foreground">Rabatt{prozent ? ` ${zahl(rabatt)} %` : ""}: − {euro(rabattBetrag)}</div>}
            <div className="font-semibold">Netto-Summe: {euro(netto)}</div>
            <div className="text-muted-foreground" data-testid="tpl-ansehen-mwst">MwSt {zahl(mwstSatz)} %: {euro(mwst)}</div>
            <div className="font-semibold" data-testid="tpl-ansehen-brutto">Brutto: {euro(netto + mwst)}</div>
          </div>
          {textLesbar(snap.schlusstext) && <p className="mt-3 px-1 text-xs text-muted-foreground whitespace-pre-line break-words" data-testid="tpl-ansehen-schlusstext">{textLesbar(snap.schlusstext)}</p>}
          {auswahl && <div className="mt-3 text-xs text-muted-foreground">Übernommen werden die Positionen und, wenn Sie das Häkchen lassen, Vor- und Schlusstext (Platzhalter wie die Anrede setzt die Suite für Ihren Kunden ein). Betreff und Kunde Ihres Belegs bleiben, wie sie sind. Der Rabatt der Vorlage gilt nur beim Ersetzen bzw. in einem leeren Beleg; beim Anhängen bleibt der Rabatt Ihres Belegs.</div>}
        </div>
        <div className="p-3 sm:p-4 border-t flex flex-col sm:flex-row justify-end gap-2">
          <button onClick={onZurueck} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted" data-testid="tpl-ansehen-zurueck">{auswahl ? "Zurück zur Liste" : "Schließen"}</button>
          {auswahl ? (
            <button onClick={onUebernehmen} disabled={!!auswahl.gesperrt} title={auswahl.gesperrt ? auswahl.gesperrtHinweis : undefined} className="disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90" data-testid="tpl-ansehen-uebernehmen">Übernehmen</button>
          ) : (
            <button onClick={onFuerKunden} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90" data-testid="tpl-ansehen-fuer-kunden">Für Kunden verwenden</button>
          )}
        </div>
      </div>
    </div>
  );
};


// ===== Rename Dialog =====
const RenameTemplateModal = ({ tpl, onClose, onSaved }) => {
  const [name, setName] = useState(tpl.name);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Name erforderlich"); return; }
    setSaving(true);
    try { await api.put(`/document-templates/${tpl.id}`, { name }); toast.success("Umbenannt"); onSaved(); }
    catch { toast.error("Fehler"); }
    finally { setSaving(false); }
  };
  return (
    <Modal isOpen={true} onClose={onClose} title="Vorlage umbenennen" size="sm">
      <div className="space-y-3">
        <label className="block text-sm font-medium">Bezeichnung</label>
        <input value={name} onChange={e => setName(e.target.value)} className="w-full border rounded-sm p-2 text-sm" autoFocus data-testid="tpl-rename-input" />
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted">Abbrechen</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50" data-testid="tpl-rename-save">
            {saving ? "Speichere..." : "Speichern"}
          </button>
        </div>
      </div>
    </Modal>
  );
};


// ===== Verwenden-Dialog (Kunde auswaehlen) =====
const UseTemplateModal = ({ tpl, onClose, onStarted }) => {
  const navigate = useNavigate();
  const [kunden, setKunden] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedKunde, setSelectedKunde] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/modules/kunden/data")
      .then(r => setKunden(r.data))
      .catch(() => toast.error("Fehler beim Laden der Kunden"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = kunden.filter(k => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (k.vorname || "").toLowerCase().includes(q) ||
           (k.nachname || "").toLowerCase().includes(q) ||
           (k.email || "").toLowerCase().includes(q);
  }).slice(0, 30);

  const create = async (withCustomer) => {
    setCreating(true);
    try {
      const body = withCustomer && selectedKunde ? { customer_id: selectedKunde.id } : {};
      const res = await api.post(`/document-templates/${tpl.id}/create-document`, body);
      toast.success(`${TYPE_META[tpl.doc_type].label} aus Vorlage erstellt`);
      onStarted();
      navigate(res.data.edit_url);
    } catch { toast.error("Fehler beim Anlegen"); setCreating(false); }
  };

  const meta = TYPE_META[tpl.doc_type] || TYPE_META.quote;

  return (
    <Modal isOpen={true} onClose={onClose} title={`Vorlage "${tpl.name}" verwenden`} size="md">
      <div className="space-y-4">
        <div className={`p-3 ${meta.bg} rounded-sm text-sm`}>
          Es wird ein neues <strong>{meta.label}</strong> mit allen Positionen und Texten aus dieser Vorlage erstellt.
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Für welchen Kunden?</label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Kunde suchen..."
              className="w-full pl-10 pr-3 py-2 border rounded-sm text-sm"
              data-testid="tpl-use-search"
            />
          </div>
          <div className="border rounded-sm max-h-64 overflow-y-auto">
            {loading ? <div className="p-3 text-sm text-muted-foreground">Lade...</div>
              : filtered.length === 0 ? <div className="p-3 text-sm text-muted-foreground">Keine Kunden gefunden.</div>
              : filtered.map(k => (
                <button
                  key={k.id}
                  onClick={() => setSelectedKunde(k)}
                  className={`w-full text-left p-2 border-b last:border-0 text-sm flex items-center gap-2 hover:bg-muted/50 ${selectedKunde?.id === k.id ? "bg-primary/5" : ""}`}
                  data-testid={`tpl-use-kunde-${k.id}`}
                >
                  {selectedKunde?.id === k.id ? <Check className="w-4 h-4 text-primary" /> : <User className="w-4 h-4 text-muted-foreground" />}
                  <span className="flex-1 truncate">{k.vorname} {k.nachname}</span>
                  <span className="text-xs text-muted-foreground truncate">{k.email}</span>
                </button>
              ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted">Abbrechen</button>
          <button
            onClick={() => create(false)}
            disabled={creating}
            className="px-4 py-2 text-sm border rounded-sm hover:bg-muted disabled:opacity-50 flex items-center gap-1 justify-center"
            data-testid="tpl-use-without-customer"
          >
            <X className="w-4 h-4" /> Ohne Kunde (Mustermann)
          </button>
          <button
            onClick={() => create(true)}
            disabled={creating || !selectedKunde}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1 justify-center"
            data-testid="tpl-use-with-customer"
          >
            <Check className="w-4 h-4" /> {creating ? "Erstelle..." : "Mit Kunde verwenden"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentTemplatesPanel;
